import * as React from 'react';

import {Avatar, Button, Card, CardActions, CircularProgress, TextField} from '@mui/material';
import {Field, withTypes} from 'react-final-form';
import {Notification, useLogin, useNotify, useTranslate} from 'react-admin';
import {useEffect, useState} from 'react';

import {ReactComponent as DvzLogo} from '../dvzLogo.svg';
import LockIcon from '@mui/icons-material/Lock';
import PropTypes from 'prop-types';
import bgImage from '../bgLogin.jpg';
import {createTheme, styled, ThemeProvider} from '@mui/material/styles';
import {dvzTheme} from './../theme';
import {getMicrosoftLoginUrl} from '../authProvider';
import {useLocation} from 'react-router-dom';

const font = {
  family: '"IBM Plex Sans", "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Arial", "sans-serif"',
};

const LoginPage = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#3cc',
  backgroundImage: `url("${bgImage}")`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  backgroundPosition: 'center bottom',
});

const LoginCard = styled(Card)({
  minWidth: 350,
  marginTop: '2em',
  textAlign: 'center',
  padding: '1rem 1.5rem',
});

const LoginAvatar = styled(Avatar)(({theme}) => ({
  backgroundColor: theme.palette.grey[300],
  width: '3.5rem',
  height: '3.5rem',
  margin: '0 auto 1rem',
}));

const LoginIcon = styled(LockIcon)(({theme}) => ({color: theme.palette.grey[500]}));
const AdminLabel = styled('div')(({theme}) => ({
  marginBottom: '2rem',
  fontSize: '0.9em',
  color: theme.palette.grey[600],
  fontWeight: 'normal',
  fontFamily: font.family,
}));
const Hint = styled('div')(({theme}) => ({
  marginTop: '-0.25rem',
  display: 'flex',
  justifyContent: 'center',
  color: theme.palette.grey[500],
  fontWeight: 500,
  textTransform: 'uppercase',
  fontFamily: font.family,
}));
const LoginFormFields = styled('div')({padding: '0 1em 1em'});
const InputContainer = styled('div')({marginTop: '1em', fontFamily: font.family});
const LoginActions = styled(CardActions)({
  padding: '0 1em 1em',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
});
const MicrosoftButton = styled(Button)({marginLeft: '0 !important'});
const LoginLogo = styled(DvzLogo)({margin: '1rem auto 0'});
const LoginTextField = styled(TextField)({
  '& .MuiInputBase-input, & .MuiInputLabel-root, & .MuiFormHelperText-root': {fontFamily: font.family},
});

const renderInput = ({
  meta: {submitFailed, error} = {submitFailed: false, error: undefined},
  input: {...inputProps},
  ...props
}) => <LoginTextField error={!!(submitFailed && error)} helperText={submitFailed && error} {...inputProps} {...props} fullWidth />;

interface FormValues {
  username?: string;
  password?: string;
}

const {Form} = withTypes<FormValues>();
const CAN_SSO_LOGIN = process.env.REACT_APP_CAN_SSO_LOGIN === 'true';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const translate = useTranslate();
  const notify = useNotify();
  const login = useLogin();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    if (error === 'sso_user_not_found') {
      notify('Kontot existerar inte', {type: 'error'});
    } else if (error === 'sso_failed') {
      notify('Inloggningen misslyckades', {type: 'error'});
    }
  }, [location.search, notify]);

  const handleSubmit = (auth: FormValues) => {
    setLoading(true);
    login(auth, location.state ? location.state.nextPathname : '/').catch((error: Error) => {
      setLoading(false);
      notify(
        typeof error === 'string'
          ? error
          : typeof error === 'undefined' || !error.message
            ? 'ra.auth.sign_in_error'
            : error.message,
        {type: 'warning'},
      );
    });
  };

  const handleMicrosoftLogin = () => {
    window.location.assign(getMicrosoftLoginUrl('/'));
  };

  const validate = (values: FormValues) => {
    const errors: FormValues = {};
    if (!values.username) {
      errors.username = translate('ra.validation.required');
    }
    if (!values.password) {
      errors.password = translate('ra.validation.required');
    }
    return errors;
  };

  return (
    <Form
      onSubmit={handleSubmit}
      validate={validate}
      render={({handleSubmit}) => (
        <form onSubmit={handleSubmit} noValidate>
          <LoginPage>
            <LoginCard>
              <LoginLogo width="240" height="100" />
              <AdminLabel>Devize Admin</AdminLabel>
              <LoginAvatar>
                <LoginIcon fontSize="large" />
              </LoginAvatar>
              <Hint>Permitify Admin</Hint>
              <LoginFormFields>
                <InputContainer>
                  <Field
                    autoFocus
                    name="username"
                    variant="standard"
                    // @ts-ignore
                    component={renderInput}
                    label={translate('ra.auth.username')}
                    disabled={loading}
                  />
                </InputContainer>
                <InputContainer>
                  <Field
                    name="password"
                    variant="standard"
                    // @ts-ignore
                    component={renderInput}
                    label={translate('ra.auth.password')}
                    type="password"
                    disabled={loading}
                  />
                </InputContainer>
              </LoginFormFields>
              <LoginActions>
                <Button variant="contained" type="submit" color="primary" disabled={loading} fullWidth>
                  {loading && <CircularProgress size={25} thickness={2} />}
                  {translate('ra.auth.sign_in')}
                </Button>
                {CAN_SSO_LOGIN ? (
                  <MicrosoftButton
                    variant="outlined"
                    type="button"
                    color="primary"
                    disabled={loading}
                    onClick={handleMicrosoftLogin}
                    fullWidth>
                    Logga in med Microsoft
                  </MicrosoftButton>
                ) : null}
              </LoginActions>
            </LoginCard>
            <Notification />
          </LoginPage>
        </form>
      )}
    />
  );
};

Login.propTypes = {
  authProvider: PropTypes.func,
  previousRoute: PropTypes.string,
};

const LoginWithTheme = (props: any) => (
  <ThemeProvider theme={createTheme(dvzTheme)}>
    <Login {...props} />
  </ThemeProvider>
);

export default LoginWithTheme;
